import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISelectItem } from '../../../store/models/select.model';
import { Subject } from 'rxjs';

export interface IPaginatorChangeEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent implements OnInit {
  @Input() length: number;
  @Input() pageSize: number;
  @Input() pageSizeOptions: number[];
  @Input() showFirstLastButtons: boolean;
  @Input() showPrevNextButtons: boolean;
  @Input() maxVisualPages = 3;
  @Input() currentPage = 1;
  @Input() totalCount = 0;
  @Input() showItemsPerPage = true;

  @Output() paginationChange: EventEmitter<IPaginatorChangeEvent>;

  onChange: Subject<IPaginatorChangeEvent> =
    new Subject<IPaginatorChangeEvent>();

  constructor() {
  }

  get change() {
    return this.onChange.asObservable();
  }

  getPageSize() {
    if (this.totalCount < this.pageSize) {
      return this.totalCount;
    }

    return this.pageSize;
  }

  ngOnInit(): void {
    if (!this.pageSizeOptions) {
      this.pageSizeOptions = [10, 20, 50, 1000];
    }
  }

  emitChanges(eventValue: IPaginatorChangeEvent): void {
    if (this.paginationChange) {
      this.paginationChange.emit(eventValue);
    }

    this.onChange.next(eventValue);
  }

  buildPageSizeSelector(): ISelectItem[] {
    return this.pageSizeOptions.map(i => ({ label: i.toString(), value: i }));
  }

  goToSpecificPage(page: number) {
    this.emitChanges({
      page,
      pageSize: this.pageSize,
    });
  }

  goToTargetPage(target: string) {
    switch (target) {
      case 'first':
        this.emitChanges({
          page: 1,
          pageSize: this.pageSize,
        });
        break;
      case 'last':
        this.emitChanges({
          page: this.length,
          pageSize: this.pageSize,
        });
        break;
      case 'next':
        if (this.currentPage < this.length) {
          this.emitChanges({
            page: this.currentPage + 1,
            pageSize: this.pageSize,
          });
        }
        break;
      case 'prev':
        if (this.currentPage > 1) {
          this.emitChanges({
            page: this.currentPage - 1,
            pageSize: this.pageSize,
          });
        }
    }
  }

  goToPage(page: string | number) {
    if (typeof page === 'number') {
      return this.goToSpecificPage(page);
    }

    this.goToTargetPage(page);
  }

  fromArrayOfNumbers(num: number) {
    return new Array(num).fill('').map((i, index) => index);
  }

  getShownPagesNumber(): number[] {
    let result: number[] = [];

    if (!this.length) {
      return result;
    }

    const middlePage = Math.round(this.maxVisualPages / 2);
    let index = 0;

    do {
      const target = this.currentPage - middlePage + index;
      index++;

      if (target <= 0) {
        continue;
      }

      if (this.currentPage - middlePage === target + 1) {
        continue;
      }

      if (target > this.length) {
        result = [];
        index = -2;
      }

      result.push(target);
    } while (result.length !== this.maxVisualPages);

    return result;
  }

  onPageSizeSelection($event: number) {
    this.emitChanges({
      page: 1, // fallback to the first page!!
      pageSize: $event,
    });
  }

  getPaginationOptions(): number[] {
    return this.pageSizeOptions;
  }
}
